
import * as React from "react";
import { ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import { DayPicker } from "react-day-picker";
import { format } from "date-fns";
import { ru } from "date-fns/locale";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function CalendarComponent({
  className,
  classNames = {},
  showOutsideDays = true,
  ...props
}: CalendarProps): JSX.Element {
  type DayPickerClassNames = NonNullable<React.ComponentProps<typeof DayPicker>["classNames"]>;
  const [selectedYear, setSelectedYear] = React.useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = React.useState(new Date().getMonth());
  const [showYearMonthSelect, setShowYearMonthSelect] = React.useState(false);

  // Diagnostics
  // eslint-disable-next-line no-console
  console.log('[Calendar] render', { selectedYear, selectedMonth, showYearMonthSelect });
  React.useEffect(() => {
    if (showYearMonthSelect) {
      try { console.log('[Calendar] year-month panel open'); } catch (e) { /* noop */ }
    }
  }, [showYearMonthSelect]);

  React.useEffect(() => {
    return () => {
      try { console.log('[Calendar] unmount'); } catch { /* noop */ }
    };
  }, []);

  // Generate years array from 1925 to current year + 10
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1925 + 11 }, (_, i) => 1925 + i);
  
  // Month names in Russian
  const months = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
  ];

  // Ensure we only spread string values to avoid widening keys to string | undefined
  const userClassNames = (classNames ?? {}) as Partial<DayPickerClassNames>;
  const filteredUserClassNames = Object.fromEntries(
    Object.entries(userClassNames).filter(([, v]) => typeof v === "string")
  ) as Partial<DayPickerClassNames>;

  const handleYearMonthChange = (year: number, month: number) => {
    setSelectedYear(year);
    setSelectedMonth(month);
    setShowYearMonthSelect(false);
  };

  return (
    <div className={cn("calendar-container", className)}>
      <style>{`
        @keyframes slideDownFade {
          from {
            opacity: 0;
            transform: translate(-50%, -4px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
      `}</style>
      
      <DayPicker
        month={new Date(selectedYear, selectedMonth)}
        onMonthChange={(monthDate) => {
          // keep DayPicker navigation in sync with our controlled month/year
          setSelectedYear(monthDate.getFullYear());
          setSelectedMonth(monthDate.getMonth());
        }}
        showOutsideDays={showOutsideDays}
        className={cn("p-3 pointer-events-auto transition-all duration-300 ease-out", className)}
        weekStartsOn={1}
        locale={ru}
        classNames={{
          root: cn(
            "calendar-container p-3",
            className
          ),
          months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
          month: "space-y-4",
          caption: "flex justify-center pt-1 relative items-center",
          caption_label: "text-sm font-medium cursor-pointer hover:text-blue-600 transition-colors duration-200",
          nav: "space-x-1 flex items-center",
          nav_button: cn(
            buttonVariants({ variant: "outline" }),
            "h-8 w-8 bg-transparent p-0 opacity-70 hover:opacity-100 hover:bg-slate-50 hover:border-slate-300 transition-all duration-200 rounded-full"
          ),
          nav_button_previous: "absolute left-1",
          nav_button_next: "absolute right-1",
          table: "w-full border-collapse space-y-1",
          head_row: "flex",
          head_cell:
            "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
          row: "flex w-full mt-2",
          cell: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
          day: cn(
            buttonVariants({ variant: "ghost" }),
            "h-9 w-9 p-0 font-normal aria-selected:opacity-100 hover:bg-slate-100 hover:text-accent-foreground transition-all duration-150 rounded-md focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
          ),
          day_range_end: "day-range-end",
          day_selected:
            "bg-blue-600 text-white hover:bg-blue-700 hover:text-white focus:bg-blue-600 focus:text-white rounded-md",
          day_today: "bg-slate-100 text-accent-foreground font-semibold border border-slate-200 rounded-md",
          day_outside:
            "day-outside text-slate-400 opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
          day_disabled: "text-slate-400 opacity-50 cursor-not-allowed",
          day_range_middle:
            "aria-selected:bg-accent aria-selected:text-accent-foreground",
          day_hidden: "invisible",
          ...filteredUserClassNames,
        }}
        components={{
          IconLeft: () => <ChevronLeft className="h-4 w-4" />,
          IconRight: () => <ChevronRight className="h-4 w-4" />,
          Caption: ({ displayMonth }) => (
            <div className="flex items-center justify-center relative w-full">
              <button
                type="button"
                onClick={() => setShowYearMonthSelect(!showYearMonthSelect)}
                onPointerDown={(e) => {
                  // Prevent parent handlers (e.g., Dialog/Popover) from treating this as outside interaction
                  e.stopPropagation();
                }}
                className="flex items-center gap-1 hover:text-blue-600 transition-colors duration-200 px-2 py-1 rounded-md hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
                aria-label="Выберите месяц и год"
                aria-expanded={showYearMonthSelect}
                aria-haspopup="true"
              >
                <span className="text-sm font-medium">
                  {format(displayMonth, 'LLLL yyyy', { locale: ru })}
                </span>
                <ChevronDown className={cn(
                  "h-3 w-3 transition-transform duration-200 ease-out",
                  showYearMonthSelect && "transform rotate-180"
                )} />
              </button>
              
              {showYearMonthSelect && (
                <div 
                  data-year-month-panel
                  className="absolute top-full mt-1 left-1/2 transform -translate-x-1/2 bg-white border border-slate-200 rounded-lg shadow-lg p-4 z-50 transition-all duration-200 ease-out animate-in slide-in-from-top-1 fade-in-0"
                  style={{
                    animation: "slideDownFade 0.2s ease-out"
                  }}
                  onPointerDown={(e) => {
                    // Ensure interactions inside the panel don't bubble to outside
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    // Safety: also stop click bubbling to avoid outside/hover interference
                    e.stopPropagation();
                  }}
                >
                  <div className="grid grid-cols-2 gap-4 min-w-[280px]">
                    <div>
                      <label className="text-xs font-medium text-slate-600 block mb-2">Год</label>
                      <Select value={selectedYear.toString()} onValueChange={(value) => setSelectedYear(parseInt(value))}>
                        <SelectTrigger className="w-full transition-all duration-200 hover:bg-slate-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-48" side="bottom" align="start">
                          {([...years].reverse()).map((year) => (
                            <SelectItem 
                              key={year} 
                              value={year.toString()}
                              className="hover:bg-slate-100 transition-colors duration-150 focus:bg-blue-50 focus:text-blue-900"
                            >
                              {year}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600 block mb-2">Месяц</label>
                      <Select value={selectedMonth.toString()} onValueChange={(value) => setSelectedMonth(parseInt(value))}>
                        <SelectTrigger className="w-full transition-all duration-200 hover:bg-slate-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent side="bottom" align="start">
                          {months.map((month, index) => (
                            <SelectItem 
                              key={index} 
                              value={index.toString()}
                              className="hover:bg-slate-100 transition-colors duration-150 focus:bg-blue-50 focus:text-blue-900"
                            >
                              {month}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 mt-4">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowYearMonthSelect(false)}
                      className="transition-all duration-200 hover:bg-slate-100 focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                    >
                      Отмена
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => handleYearMonthChange(selectedYear, selectedMonth)}
                      className="bg-blue-600 hover:bg-blue-700 transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                    >
                      Применить
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ),
        }}
        {...props}
      />
    </div>
  );
}
CalendarComponent.displayName = "Calendar";

const areCalendarPropsEqual = (prev: CalendarProps, next: CalendarProps) => {
  // Shallow compare props likely to change; DayPicker props are mostly stable here.
  return prev.className === next.className && prev.showOutsideDays === next.showOutsideDays;
};

export const Calendar = React.memo(CalendarComponent, areCalendarPropsEqual);
